import { Link } from "react-router-dom";
import { VerifiedBadge } from "@/components/market/AndroidVerification";
import { RatingBadge } from "@/components/reviews/Stars";
import { ChevronRight, Download, UserRound } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { AppIcon } from "@/components/apps/AppIcon";
import { PlatformBadge, PlatformDownload } from "@/components/apps/Platform";
import { AboutPanel } from "@/components/apps/AboutPanel";
import { vLabel } from "@/lib/version";
import { MinisiteBadge } from "@/components/apps/MinisiteInfo";
import { NoveltyBanner, noveltyTeaser } from "@/components/apps/NoveltyBanner";
import { BrandTag } from "@/components/apps/BrandTag";
import { MegaScroller } from "@/components/apps/MegaScroller";

export const AppCard = ({ app, index = 0, hideNovelty = false }) => {
  const { t, lang, fmtBytes, fmtDate, fmtDownloads } = useI18n();
  const novelty = !hideNovelty && app.novelty && !app.developer;
  const v = app.active_version;
  return (
    <article
      className="card-carbon hoverable fade-up relative flex flex-col overflow-hidden rounded-2xl p-5"
      style={{ animationDelay: `${index * 70}ms` }}
      data-testid={`app-card-${app.id}`}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[var(--red)] via-[var(--gold)] to-[var(--red)]" />
      {novelty && app.novelty.banner !== false && <div className="mb-3"><NoveltyBanner app={app} testId={`app-card-novelty-${app.id}`} /></div>}
      <div className="mb-3 flex flex-wrap items-center gap-2"><PlatformBadge app={app} testId={`app-card-platform-${app.id}`} /><BrandTag app={app} testId={`app-card-brand-${app.id}`} /></div>
      <Link to={app.developer ? `/developpeurs/apps/${app.id}` : `/apps/${app.id}`} className="flex items-start gap-4" data-testid={`app-card-link-${app.id}`}>
        <AppIcon app={app} />
        <div className="min-w-0 flex-1">
          <h3 className="break-words hyphens-auto font-display text-sm font-bold uppercase tracking-wide text-white sm:text-base" data-testid="app-card-name">
            {app.name}
          </h3>
          <p className="mt-1 line-clamp-2 break-words [overflow-wrap:anywhere] text-sm text-neutral-400" data-testid="app-card-description">
            {app.description || "—"}
          </p>
        </div>
        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-[var(--gold)] rtl:-scale-x-100" />
      </Link>
      {app.developer && (
        <Link to={`/developpeurs/${app.developer.slug}`} className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full border border-amber-400/50 px-2.5 py-1 text-xs text-amber-200 hover:text-amber-100" data-testid={`app-card-developer-${app.id}`}>
          <UserRound className="h-3.5 w-3.5" /> {t("market.by", { name: app.developer.brand })}
        </Link>
      )}
      {novelty && <p className="mt-3 line-clamp-3 break-words text-xs font-semibold text-[var(--gold-bright)]" data-testid={`app-card-teaser-${app.id}`}>{noveltyTeaser(app, t, lang)}</p>}
      {novelty && app.mega && <div className="mt-3 min-w-0"><MegaScroller mega={app.mega} testId={`app-card-mega-${app.id}`} /></div>}
      {app.category === "minisite" && <div className="mt-3"><MinisiteBadge thirdParty={!!app.developer} testId={`app-card-minisite-${app.id}`} /></div>}
      {app.developer?.android_verified && <div className="mt-2"><VerifiedBadge testId={`app-card-verified-${app.id}`} /></div>}
      <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--gold-bright)]" data-testid={`app-card-downloads-${app.id}`}>
        <Download className="h-3.5 w-3.5" /> {fmtDownloads(app.downloads)}
      </p>
      <div className="mt-1.5"><RatingBadge rating={app.rating} testId={`app-card-rating-${app.id}`} /></div>
      {v && (
        <dl className="mt-4 grid grid-cols-3 gap-2 text-xs" data-testid="app-card-meta">
          <div>
            <dt className="text-neutral-500">{t("apps.version")}</dt>
            <dd className="font-display font-bold text-[var(--gold-bright)]" data-testid="app-card-version">{vLabel(v)}</dd>
          </div>
          <div>
            <dt className="text-neutral-500">{t("apps.size")}</dt>
            <dd className="text-neutral-200" data-testid="app-card-size">{fmtBytes(v.size)}</dd>
          </div>
          <div>
            <dt className="text-neutral-500">{t("apps.date")}</dt>
            <dd className="text-neutral-200" data-testid="app-card-date">{fmtDate(v.created_at)}</dd>
          </div>
        </dl>
      )}
      <div className="mt-5 flex flex-1 items-end">
        <PlatformDownload app={app} testId={`download-btn-${app.id}`} />
      </div>
      {app.active_version && <AboutPanel about={app.about} testId={`about-panel-${app.id}`} />}
    </article>
  );
};
